import { createRoot } from "react-dom/client";
import "./index.css";

// ============================================================
// FAIL-SAFE BOOT SYSTEM
// Ensures the app NEVER shows a blank white screen on iOS
// ============================================================

const BOOTSTRAP_TIMEOUT_MS = 2500; // 2.5 seconds max for bootstrap

/**
 * Root element resolver.
 * Never assume #root exists at module-eval time (prevents early crash => white screen).
 */
let ROOT_ELEMENT: HTMLElement | null = null;
function ensureRootElement(): HTMLElement {
  if (ROOT_ELEMENT) return ROOT_ELEMENT;

  const el = document.getElementById("root");
  if (el) {
    ROOT_ELEMENT = el;
    return el;
  }

  // Fallback: create a root if missing (should not happen, but prevents hard crash)
  const fallback = document.createElement("div");
  fallback.id = "root";
  document.body.appendChild(fallback);
  ROOT_ELEMENT = fallback;
  return fallback;
}

/**
 * Ultra-safe native detection (avoids depending on imports during boot).
 */
function safeIsCapacitorNative(): boolean {
  try {
    const cap = (window as any).Capacitor;
    if (!cap) return false;
    const platform = typeof cap.getPlatform === "function" ? cap.getPlatform() : undefined;
    const isNativeByPlatform = platform === "ios" || platform === "android";
    const isNativeByAPI = typeof cap.isNativePlatform === "function" ? !!cap.isNativePlatform() : false;
    return isNativeByPlatform || isNativeByAPI;
  } catch {
    return false;
  }
}

/**
 * Show a minimal boot screen immediately
 * This prevents the white screen even if everything else fails
 */
function showBootScreen(message: string = "Φόρτωση..."): void {
  const root = ensureRootElement();
  root.innerHTML = `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      min-height: 100dvh;
      background: linear-gradient(135deg, #E8F0FE 0%, #F8FAFC 100%);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      color: #1e3a5f;
      padding: 20px;
      box-sizing: border-box;
    ">
      <div style="
        width: 48px;
        height: 48px;
        border: 4px solid #e2e8f0;
        border-top-color: #2563eb;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      "></div>
      <p style="margin-top: 16px; font-size: 16px; opacity: 0.8;">${message}</p>
    </div>
    <style>
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    </style>
  `;
}

/**
 * Show an error screen with diagnostic info
 */
function showErrorScreen(error: Error | string, context: string): void {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : '';
  
  console.error(`[main] FATAL ERROR in ${context}:`, error);
  
  const root = ensureRootElement();
  root.innerHTML = `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      min-height: 100dvh;
      background: linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      color: #991b1b;
      padding: 20px;
      box-sizing: border-box;
      text-align: center;
    ">
      <div style="
        width: 64px;
        height: 64px;
        background: #fee2e2;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 16px;
      ">
        <span style="font-size: 32px;">⚠️</span>
      </div>
      <h1 style="font-size: 20px; margin: 0 0 8px 0;">Σφάλμα Εκκίνησης</h1>
      <p style="font-size: 14px; opacity: 0.8; margin: 0 0 16px 0;">Κάτι πήγε στραβά κατά την εκκίνηση</p>
      <div style="
        background: #fff;
        border: 1px solid #fecaca;
        border-radius: 8px;
        padding: 12px;
        max-width: 100%;
        overflow: auto;
        text-align: left;
        font-size: 12px;
        font-family: monospace;
        color: #7f1d1d;
        margin-bottom: 16px;
      ">
        <strong>Context:</strong> ${context}<br>
        <strong>Error:</strong> ${errorMessage}
        ${errorStack ? `<br><br><strong>Stack:</strong><br>${errorStack.split('\n').slice(0, 5).join('<br>')}` : ''}
      </div>
      <button onclick="window.location.reload()" style="
        background: #dc2626;
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 16px;
        cursor: pointer;
      ">Δοκιμάστε Ξανά</button>
    </div>
  `;
}

/**
 * Setup global error handlers to catch uncaught errors in iOS WebView
 */
function setupGlobalErrorHandlers(): void {
  window.addEventListener('error', (event) => {
    console.error('[main] Uncaught error:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error
    });
    
    // Only show error screen if app hasn't rendered yet
    const root = ensureRootElement();
    if (!root.querySelector('[data-app-rendered]')) {
      showErrorScreen(event.error || event.message, 'window.onerror');
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    console.error('[main] Unhandled promise rejection:', event.reason);
    
    // Only show error screen if app hasn't rendered yet
    const root = ensureRootElement();
    if (!root.querySelector('[data-app-rendered]')) {
      showErrorScreen(event.reason, 'unhandledrejection');
    }
  });
}

/**
 * Create a timeout promise for race conditions
 */
function createTimeout<T>(ms: number, message: string): Promise<T> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`Timeout: ${message} (${ms}ms)`)), ms);
  });
}

/**
 * Native App Bootstrap with timeout protection
 */
async function bootstrapNative(): Promise<void> {
  const startTime = Date.now();
  console.log('[bootstrap] ========== NATIVE COLD START ==========');
  console.log('[bootstrap] Start time:', new Date().toISOString());
  
  try {
    // Step 1: Import storage utilities and warm up
    console.log('[bootstrap] Step 1: Importing storage utilities...');
    const storageModule = await Promise.race([
      import('@/lib/nativeStorage'),
      createTimeout<never>(1500, 'Storage module import')
    ]);
    console.log('[bootstrap] Step 1 complete:', Date.now() - startTime, 'ms');
    
    const { warmUpStorage } = storageModule;
    
    // Step 1.5: Warm up native storage
    console.log('[bootstrap] Step 1.5: Warming up native storage...');
    await warmUpStorage();
    console.log('[bootstrap] Step 1.5 complete:', Date.now() - startTime, 'ms');
    
    // FAST PATH: Check if session token exists. If not, skip heavy steps.
    const PROJECT_ID = 'zytlfsygqaqvdjrvgbjv';
    const SESSION_KEY = `sb-${PROJECT_ID}-auth-token`;
    let hasSession = false;
    
    try {
      const { Preferences } = await import('@capacitor/preferences');
      const tokenResult = await Promise.race([
        Preferences.get({ key: SESSION_KEY }),
        createTimeout<never>(500, 'Quick token check')
      ]);
      hasSession = !!(tokenResult as any)?.value;
      console.log('[bootstrap] Quick token check:', hasSession ? 'TOKEN FOUND' : 'NO TOKEN (fresh install)');
    } catch (e) {
      console.warn('[bootstrap] Quick token check failed:', e);
    }
    
    if (!hasSession) {
      // Fresh install - skip migration and heavy steps
      console.log('[bootstrap] FAST PATH: Fresh install, skipping migration & storage enumeration');
      console.log('[bootstrap] ========== BOOTSTRAP COMPLETE (FAST) ==========');
      console.log('[bootstrap] Total time:', Date.now() - startTime, 'ms');
      return;
    }
    
    // Returning user - run migration and diagnostics
    const { migrateLocalStorageToNative } = storageModule;
    
    // Step 2: Run migration (non-blocking, best effort)
    console.log('[bootstrap] Step 2: Running migration...');
    try {
      await Promise.race([
        migrateLocalStorageToNative(),
        createTimeout<never>(1000, 'Migration')
      ]);
    } catch (migrationError) {
      console.warn('[bootstrap] Migration timeout/error (non-fatal):', migrationError);
    }
    console.log('[bootstrap] Step 2 complete:', Date.now() - startTime, 'ms');
    
    // Step 3: Check activeRole (non-blocking)
    console.log('[bootstrap] Step 3: Checking activeRole...');
    try {
      const { getActiveRole } = await import('@/lib/appStorage');
      const storedRole = await getActiveRole();
      console.log('[bootstrap] Stored activeRole:', storedRole || 'null');
    } catch (roleError) {
      console.warn('[bootstrap] Could not check role (non-fatal):', roleError);
    }
    console.log('[bootstrap] Step 3 complete:', Date.now() - startTime, 'ms');
    
    console.log('[bootstrap] ========== BOOTSTRAP COMPLETE ==========');
    console.log('[bootstrap] Total time:', Date.now() - startTime, 'ms');
    
  } catch (error) {
    console.error('[bootstrap] Bootstrap failed:', error);
  }
}

/**
 * Verify session after app loads (for debugging)
 */
async function verifySession(): Promise<void> {
  try {
    const { supabase } = await import('@/integrations/supabase/nativeClient');
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session) {
      console.log('[main] ✓ Session found after bootstrap:', session.user.id);
    } else {
      console.log('[main] ✗ No session found after bootstrap');
    }
  } catch (error) {
    console.error('[main] Error verifying session:', error);
  }
}

/**
 * Start the application with full error protection
 */
async function startApp(): Promise<void> {
  const startTime = Date.now();
  const isNative = safeIsCapacitorNative();
  
  // IMMEDIATELY show boot screen - prevents white screen
  showBootScreen();
  console.log('[main] Boot screen displayed');
  
  // Setup global error handlers
  setupGlobalErrorHandlers();
  console.log('[main] Global error handlers installed');
  
  // Native bootstrap with timeout
  if (isNative) {
    console.log('[main] Native platform detected, running bootstrap...');
    
    try {
      await Promise.race([
        bootstrapNative(),
        createTimeout<void>(BOOTSTRAP_TIMEOUT_MS, 'Bootstrap')
      ]);
      console.log('[main] Bootstrap complete');
    } catch (err) {
      console.warn('[main] Bootstrap timeout/error (continuing anyway):', err);
      // Continue anyway - the app should still work
    }
  } else {
    console.log('[main] Web platform, skipping native bootstrap');
  }
  
  // Import App component with timeout
  console.log('[main] Importing App component...');
  let App;
  try {
    const appModule = await Promise.race([
      import("./App"),
      createTimeout<never>(5000, 'App import')
    ]);
    App = appModule.default;
    console.log('[main] App imported successfully');
  } catch (importError) {
    console.error('[main] Failed to import App:', importError);
    showErrorScreen(importError as Error, 'App import');
    return;
  }
  
  // Render the app
  console.log('[main] Rendering app...');
  try {
    const rootEl = ensureRootElement();
    // Mark root as rendered BEFORE React mount to trigger CSS reset
    rootEl.setAttribute('data-app-rendered', 'true');
    const root = createRoot(rootEl);
    root.render(<App />);
    console.log('[main] App rendered successfully in', Date.now() - startTime, 'ms');
  } catch (renderError) {
    console.error('[main] Failed to render App:', renderError);
    showErrorScreen(renderError as Error, 'React render');
    return;
  }
  
  // Verify session after render (for debugging)
  if (isNative) {
    setTimeout(() => verifySession(), 500);
  }
}

// Start the app immediately
startApp();
